// Shapes of the login contract (POST /login/user and the magic-link
// GET /login/magic/status "approved" payload). One type so login.tsx and
// LoginLinkButton.tsx funnel the same object into completeLogin.

/** What the account still has to set before it is fully migrated (2026-10-19).
 *  The BE computes these from the user document; any true flag sends the
 *  user to /account-setup before anything else. */
export interface AccountSetupFlags {
    /** No login handle yet — a legacy colour + PIN account. */
    username: boolean;
    /** Manager/Admin without an email on file (needed for email login + links). */
    email: boolean;
    /** Same condition as `username`: the stored secret is a PIN, choose a policy password. */
    password: boolean;
}

export interface LoginResponse {
    uid: number;
    /** WIRE: this is the DISPLAY NAME (legacy key name). The login handle is `loginUsername`. */
    username: string;
    /** Login handle, or null for an account that has not been through setup. */
    loginUsername?: string | null;
    colour?: string | null;
    warehouse: string;
    /** Stored value — a position name or a legacy rank. Prefer `position`. */
    permissions: string;
    /** Normalised position name, when the BE returns one. */
    position?: string;
    /** Saved UI language ("en" | "pl") or null — applied by completeLogin. */
    language?: string | null;
    requirePasswordChange?: boolean;
    /** Why requirePasswordChange is set (2026-10-20): an admin flag / policy rollout
     *  ("forced"), or a password older than 90 days ("expired"). Wording only — the
     *  flag is what gates. Absent from older BE builds. */
    passwordChangeReason?: "forced" | "expired" | null;
    needsAccountSetup?: boolean;
    accountSetup?: AccountSetupFlags;
    sessionToken?: string;
}

/** POST /login/user (or the magic-link status) when the password passed but a second factor is
 *  still owed (2026-10-20). No session exists yet: `challengeToken` authenticates the three
 *  /login/2fa/* challenge routes as `Authorization: Bearer`, for `expiresInSeconds`. */
export interface TwoFactorChallenge {
    twoFactorRequired: true;
    /** True = never enrolled (or reset by an admin): show the QR enrolment instead of the code box. */
    enrolmentRequired: boolean;
    challengeToken: string;
    expiresInSeconds: number;
    user: { name: string; loginUsername: string | null };
}

/** POST /login/2fa/verify and /enrol/confirm: the login payload plus the trust extras. */
export interface TwoFactorCompletion extends LoginResponse {
    /** 64-hex token that skips the code on this device for 7 days (helpers/twoFactorTrust). Null when the user opted out. */
    trustToken?: string | null;
    usedBackupCode?: boolean;
    backupCodesRemaining?: number | null;
    /** Enrolment only. Shown once; the user must acknowledge before continuing. */
    backupCodes?: string[];
}

export const isTwoFactorChallenge = (r: unknown): r is TwoFactorChallenge =>
    typeof r === "object" && r !== null
    && (r as TwoFactorChallenge).twoFactorRequired === true
    && typeof (r as TwoFactorChallenge).challengeToken === "string";

/** GET /login/methods */
export interface LoginMethods {
    colourLogin: boolean;
    identifierLogin?: boolean;
    /** May the browser save / fill sign-in details? False unless an Admin allowed it (Settings › Login screen). */
    savedLogins?: boolean;
    passwordPolicy?: {
        managerPlus: { minLength: number };
        standard: { minLength: number };
        /** Every password must be replaced this many days after it was set. */
        maxAgeDays?: number;
    };
    username?: { minLength: number; maxLength: number; pattern: string };
}

/** GET /login/username/available?u= */
export interface UsernameAvailabilityResponse {
    available: boolean;
    code?: string;
    username?: string;
    error?: string;
}
